import { z } from "zod";
import { consensusBallotViewSchema, seatVotedByOtherSchema } from "./vote.js";

/* ===========================================================================
 * US-E19.F6-0205 — LIFECYCLE: dọn treo · force-transfer owner mất tích · deceased-forgive ·
 * dormant-revive. @UC9316 @UC9317 @UC9318 @UC9319
 *
 * 🔴 NGUYÊN TẮC KHẮC VÀO CONTRACT:
 *  - MỌI đường thoát (force-transfer / deceased-forgive / dormant-revive) BẮT BUỘC `consensusId`
 *    (phiên đồng thuận nhóm ĐÃ PASSED). KHÔNG có shape nào cho phép một cá nhân tự quyết.
 *  - `DeceasedForgiveDto` KHÔNG nhận `amount` (server tự tính nợ hiện có) ⇒ không ai "tha" được số
 *    tiền do mình bịa ra. `.strict()` ⇒ gửi `amountMinor` = 400 tại BIÊN.
 *  - Tiền (nếu xuất hiện ở response) = **chuỗi BigInt minor** (B1 no-float).
 * ========================================================================= */

/** Loại quyết định LIFECYCLE — cột `Consensus.lifecycleKind` (CHECK DB cùng whitelist). */
export const LIFECYCLE_KIND = {
  /** Chuyển quyền owner mất tích (passed = nhóm đồng ý; áp hành động ở endpoint riêng). */
  OWNER_FORCE_TRANSFER: "owner_force_transfer",
  /** Xoá nợ người đã mất (passed = nhóm đồng ý; forgive = bút toán ĐỐI ỨNG, KHÔNG xoá sổ cái). */
  DECEASED_FORGIVE: "deceased_forgive",
  /** Hồi sinh nhóm ngủ đông (đối chiếu member còn/mất — KHÔNG tự khôi phục quyền). */
  DORMANT_REVIVE: "dormant_revive",
} as const;

export type LifecycleKind = (typeof LIFECYCLE_KIND)[keyof typeof LIFECYCLE_KIND];

export const lifecycleKindSchema = z.enum([
  LIFECYCLE_KIND.OWNER_FORCE_TRANSFER,
  LIFECYCLE_KIND.DECEASED_FORGIVE,
  LIFECYCLE_KIND.DORMANT_REVIVE,
]);

/** Khoá config (system_settings) — mặc định TẮT/AN-TOÀN (0 = tắt, xem migration S01). */
export const OWNER_INACTIVE_DAYS_KEY = "owner_inactive_days";
export const OWNER_OBJECTION_WINDOW_DAYS_KEY = "owner_objection_window_days";
export const STUCK_VOTE_TTL_DAYS_KEY = "stuck_vote_ttl_days";

/** Trạng thái yêu cầu chuyển quyền (mirror CHECK owner_transfer_requests_status_check). */
export const OWNER_TRANSFER_REQUEST_STATUS = {
  OBJECTION_WINDOW: "objection_window",
  VOTING: "voting",
  APPLIED: "applied",
  CANCELLED: "cancelled",
} as const;

export type OwnerTransferRequestStatus =
  (typeof OWNER_TRANSFER_REQUEST_STATUS)[keyof typeof OWNER_TRANSFER_REQUEST_STATUS];

export const ownerTransferRequestStatusSchema = z.enum([
  OWNER_TRANSFER_REQUEST_STATUS.OBJECTION_WINDOW,
  OWNER_TRANSFER_REQUEST_STATUS.VOTING,
  OWNER_TRANSFER_REQUEST_STATUS.APPLIED,
  OWNER_TRANSFER_REQUEST_STATUS.CANCELLED,
]);

export const LIFECYCLE_REASON_MAX = 500;

/* ---------------------------------------------------------------------------
 * 1) ĐỀ NGHỊ chuyển quyền (owner mất tích) — mở CỬA SỔ PHẢN ĐỐI, CHƯA có vote, CHƯA đổi chủ.
 * ------------------------------------------------------------------------- */
export const openOwnerTransferRequestSchema = z
  .object({
    /** GroupMember.id của người được đề cử làm owner mới. PHẢI khác người khởi xướng (server + CHECK DB). */
    newOwnerMemberId: z.string().uuid(),
    reason: z.string().min(1).max(LIFECYCLE_REASON_MAX),
  })
  .strict();

export type OpenOwnerTransferRequestBody = z.infer<typeof openOwnerTransferRequestSchema>;

export const ownerTransferRequestResponseSchema = z
  .object({
    requestId: z.string().uuid(),
    groupId: z.string().uuid(),
    initiatorMemberId: z.string().uuid(),
    newOwnerMemberId: z.string().uuid(),
    currentOwnerMemberId: z.string().uuid(),
    status: ownerTransferRequestStatusSchema,
    /** Hết cửa sổ phản đối — TRƯỚC mốc này KHÔNG mở được phiên biểu quyết. */
    objectionEndsAt: z.string().datetime(),
    /** Gắn khi đã mở phiên đồng thuận (objection_window ⇒ null). */
    consensusId: z.string().uuid().nullable(),
    /** true KHI VÀ CHỈ KHI quyền đã thật sự chuyển (reuse OwnershipTransferService). */
    ownershipTransferred: z.boolean(),
  })
  .strict();

export type OwnerTransferRequestResponse = z.infer<typeof ownerTransferRequestResponseSchema>;

/* ---------------------------------------------------------------------------
 * 2) FORCE-TRANSFER (thực thi) — BẮT BUỘC `consensusId` đã PASSED.
 *    KHÔNG có biến thể nào bỏ qua đồng thuận: `consensusId` là field BẮT BUỘC của schema.
 * ------------------------------------------------------------------------- */
export const forceTransferOwnershipSchema = z
  .object({
    consensusId: z.string().uuid(),
    reason: z.string().min(1).max(LIFECYCLE_REASON_MAX),
  })
  .strict();

export type ForceTransferOwnershipBody = z.infer<typeof forceTransferOwnershipSchema>;

/* ---------------------------------------------------------------------------
 * 3) DECEASED-FORGIVE — xoá nợ người đã mất QUA ĐỒNG THUẬN.
 *    🔴 KHÔNG có field `amount*`: server tính nợ hiện có (đọc DebtEntry append-only + member_dues).
 *    `.strict()` ⇒ client gửi `amountMinor` ⇒ 400 (không ai tự đặt số tiền được tha).
 * ------------------------------------------------------------------------- */
export const deceasedForgiveSchema = z
  .object({
    deceasedMemberId: z.string().uuid(),
    consensusId: z.string().uuid(),
    reason: z.string().min(1).max(LIFECYCLE_REASON_MAX),
  })
  .strict();

export type DeceasedForgiveBody = z.infer<typeof deceasedForgiveSchema>;

export const deceasedForgiveResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    deceasedMemberId: z.string().uuid(),
    consensusId: z.string().uuid(),
    /** Σ nợ peer đã tha (chuỗi BigInt minor — B1 no-float). */
    forgivenPeerDebtMinor: z.string(),
    /** Số khoản thu (member_due) chuyển 'waived' — KHÔNG xoá hàng nào. */
    waivedDueCount: z.number().int().nonnegative(),
    /** Bút toán ĐỐI ỨNG đã ghi (append-only). Rỗng ⇒ không còn nợ peer để tha. */
    ledgerTxIds: z.array(z.string().uuid()),
    /** 🔴 Khẳng định trong hợp đồng: KHÔNG dòng sổ cái nào bị sửa/xoá. */
    ledgerPreserved: z.literal(true),
  })
  .strict();

export type DeceasedForgiveResponse = z.infer<typeof deceasedForgiveResponseSchema>;

/* ---------------------------------------------------------------------------
 * 4) DORMANT-REVIVE — đối chiếu member còn/mất. KHÔNG tự khôi phục quyền OWNER/quyền tiền.
 * ------------------------------------------------------------------------- */
export const reconciledMemberStatusSchema = z.enum(["active", "left", "deceased"]);
export type ReconciledMemberStatus = z.infer<typeof reconciledMemberStatusSchema>;

export const dormantReviveSchema = z
  .object({
    consensusId: z.string().uuid(),
    reconciledMembers: z
      .array(
        z
          .object({
            memberId: z.string().uuid(),
            status: reconciledMemberStatusSchema,
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export type DormantReviveBody = z.infer<typeof dormantReviveSchema>;

export const dormantReviveResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    consensusId: z.string().uuid(),
    dormantState: z.enum(["active", "dormant"]),
    revivedAt: z.string().datetime(),
    reconciledCount: z.number().int().nonnegative(),
    /** 🔴 Revive KHÔNG cấp lại quyền: không ai được lên owner/treasurer qua đường này. */
    rolesGranted: z.literal(0),
  })
  .strict();

export type DormantReviveResponse = z.infer<typeof dormantReviveResponseSchema>;

/* ---------------------------------------------------------------------------
 * 5) Mở phiên đồng thuận lifecycle (deceased_forgive / dormant_revive) + bỏ phiếu.
 *    🔴 `owner_force_transfer` KHÔNG mở được qua đây: phiên đó chỉ sinh ra từ YÊU CẦU đã qua cửa sổ
 *    phản đối (endpoint riêng) ⇒ không có đường tắt mở thẳng phiếu đoạt quyền.
 * ------------------------------------------------------------------------- */
/**
 * 🔴🔴 DECEASED-SESSION-SUBJECT (review #1332 879081736 · QĐ PO 08/10 vòng 15 · @UC9319) — union PHÂN BIỆT theo `lifecycleKind`:
 *  · `deceased_forgive` BẮT BUỘC `deceasedMemberId` — phiên biểu quyết cho ĐÚNG MỘT người mất; người biểu quyết thấy người đó (response đọc phiên) và
 *    cửa áp chỉ nhận đúng người đó (`DECEASED_SUBJECT_MISMATCH`). Trước: phiên không mang chủ thể ⇒ một phiên passed bất kỳ = mọi thành viên xoá nợ của
 *    BẤT KỲ AI, kể cả chính mình.
 *  · `dormant_revive` CẤM `deceasedMemberId` (`.strict()` ⇒ 400 ở biên).
 */
const openLifecycleDecisionBase = {
  title: z.string().min(1).max(200),
  reason: z.string().min(1).max(LIFECYCLE_REASON_MAX),
} as const;

export const openLifecycleDecisionSchema = z.discriminatedUnion("lifecycleKind", [
  z
    .object({
      lifecycleKind: z.literal(LIFECYCLE_KIND.DECEASED_FORGIVE),
      deceasedMemberId: z.string().uuid(),
      ...openLifecycleDecisionBase,
    })
    .strict(),
  z
    .object({
      lifecycleKind: z.literal(LIFECYCLE_KIND.DORMANT_REVIVE),
      ...openLifecycleDecisionBase,
    })
    .strict(),
]);

export type OpenLifecycleDecisionBody = z.infer<typeof openLifecycleDecisionSchema>;

export const lifecycleDecisionResponseSchema = z
  .object({
    decisionId: z.string().uuid(),
    groupId: z.string().uuid(),
    lifecycleKind: lifecycleKindSchema,
    /**
     * 🔴 DECEASED-SESSION-SUBJECT — người mất mà phiên `deceased_forgive` biểu quyết (ghế `group_members.id`), để người bỏ phiếu THẤY mình đang quyết cho
     * ai. `null` ⇒ phiên loại khác, hoặc phiên `deceased_forgive` CŨ không mang chủ thể (không áp được — `DECEASED_SUBJECT_MISMATCH`).
     */
    deceasedMemberId: z.string().uuid().nullable(),
    status: z.enum(["open", "passed", "failed", "expired"]),
    tally: z
      .object({
        yes: z.number().int().nonnegative(),
        no: z.number().int().nonnegative(),
        abstain: z.number().int().nonnegative(),
        totalEligible: z.number().int().nonnegative(),
      })
      .strict(),
    myVote: z.enum(["yes", "no", "abstain"]).nullable(),
    /** 🔴 passed ≠ đã thực thi — hành động còn phải qua endpoint riêng (kiểm cửa sổ + owner im lặng). */
    applied: z.boolean(),
    /**
     * US-AIV2-0112 CONSENSUS-FIRST-VOTE (@UC0356): CHỈ có ở response BỎ PHIẾU — suất đã có phiếu của người khác, lần bấm này KHÔNG ghi
     * cho chúng. Vắng ở response đọc phiên (GET).
     */
    seatsVotedByOthers: z.array(seatVotedByOtherSchema).optional(),
  })
  .strict();

export type LifecycleDecisionResponse = z.infer<typeof lifecycleDecisionResponseSchema>;

/**
 * Response ĐỌC phiên (`GET /lifecycle/decisions/:id`) = response phiên + lá phiếu của người xem (US-AIV2-0112 CONSENSUS-MOBILE ·
 * @UC0357). `ballot` BẮT BUỘC ở cửa đọc; response mở phiên / bỏ phiếu giữ `lifecycleDecisionResponseSchema`.
 */
export const lifecycleDecisionDetailResponseSchema = lifecycleDecisionResponseSchema
  .extend({ ballot: consensusBallotViewSchema })
  .strict();

export type LifecycleDecisionDetailResponse = z.infer<typeof lifecycleDecisionDetailResponseSchema>;

export const castLifecycleVoteSchema = z
  .object({ choice: z.enum(["yes", "no", "abstain"]) })
  .strict();

export type CastLifecycleVoteBody = z.infer<typeof castLifecycleVoteSchema>;

/* ---------------------------------------------------------------------------
 * 6) Mã lỗi + thông điệp (VN, không lộ nội bộ).
 * ------------------------------------------------------------------------- */
export const LIFECYCLE_CODE = {
  /** Ngưỡng chưa được BẬT (owner_inactive_days = 0) ⇒ KHÔNG force-transfer (fail-safe). */
  LIFECYCLE_DISABLED: "LIFECYCLE_DISABLED",
  /** Owner vẫn còn hoạt động trong ngưỡng ⇒ không ai được đề nghị chuyển quyền. */
  OWNER_STILL_ACTIVE: "OWNER_STILL_ACTIVE",
  /** Còn trong cửa sổ phản đối ⇒ chưa được mở phiên / chưa được chuyển quyền. */
  OBJECTION_WINDOW_OPEN: "OBJECTION_WINDOW_OPEN",
  /** Phiên đồng thuận CHƯA đạt (open/failed/expired) ⇒ KHÔNG áp hành động. */
  CONSENSUS_NOT_REACHED: "CONSENSUS_NOT_REACHED",
  /** Chỉ có phiếu thuận của chính người khởi xướng ⇒ KHÔNG cho tự phê duyệt mình. */
  SELF_APPROVAL_FORBIDDEN: "SELF_APPROVAL_FORBIDDEN",
  /** Đã có 1 yêu cầu đang mở cho nhóm này. */
  REQUEST_ALREADY_OPEN: "REQUEST_ALREADY_OPEN",
  /** Yêu cầu đã bị huỷ (owner quay lại / hết hạn). */
  REQUEST_CANCELLED: "REQUEST_CANCELLED",
  /**
   * 🔴 DECEASED-FORGIVE-409 — chạy lại phiên xoá nợ người mất ĐÃ áp (QĐ PO 08/10 cho chạy lại) mà người mất có nợ MỚI với CÙNG người cho nợ trong CÙNG
   * quỹ: phiên này đã tha cặp đó một lần (khoá `deceased-forgive:<phiên>:<người cho nợ>`), nợ mới cần MỘT PHIÊN BIỂU QUYẾT MỚI. 0 ghi (rollback toàn tx).
   */
  DECEASED_FORGIVE_NEEDS_NEW_SESSION: "DECEASED_FORGIVE_NEEDS_NEW_SESSION",
  /**
   * 🔴 DECEASED-SESSION-SUBJECT (QĐ PO 08/10 vòng 15) — 409: áp xoá nợ cho người KHÁC người mà phiên đã biểu quyết, HOẶC phiên `deceased_forgive` không
   * mang chủ thể (phiên cũ — fail-closed, không backfill). 0 ghi. Lối ra: mở phiên mới cho đúng người.
   */
  DECEASED_SUBJECT_MISMATCH: "DECEASED_SUBJECT_MISMATCH",
  /**
   * 🔴 DECEASED-SESSION-SUBJECT — 409: người MỞ phiên hoặc người ÁP chính là người đại diện ghế người mất (chính chủ `primaryUserId` HOẶC liên kết sống —
   * cùng vị-từ "người của suất"): không ai tự quyết xoá nợ/miễn khoản của chính suất mình. 0 ghi.
   */
  DECEASED_SUBJECT_IS_SELF: "DECEASED_SUBJECT_IS_SELF",
  /**
   * 🔴 DECEASED-SESSION-FOLLOWUPS (QĐ PO 08/10 vòng 17 "Loại suất đó khỏi biểu quyết" · @UC9319) — 409: lần bấm phiếu chỉ còn rơi vào SUẤT NGƯỜI MẤT của
   * phiên `deceased_forgive` (suất đó không nằm trong mẫu số và không có phiếu). 0 phiếu, 0 audit. Người đại diện còn suất KHÁC vẫn bỏ phiếu cho suất
   * kia (chỉ suất người mất bị loại, không loại người).
   */
  DECEASED_SUBJECT_CANNOT_VOTE: "DECEASED_SUBJECT_CANNOT_VOTE",
} as const;

export const LIFECYCLE_DISABLED_MESSAGE =
  "Tính năng chuyển quyền khi chủ nhóm vắng mặt chưa được bật.";
export const OWNER_STILL_ACTIVE_MESSAGE =
  "Chủ nhóm vẫn đang hoạt động — không thể đề nghị chuyển quyền.";
export const OBJECTION_WINDOW_OPEN_MESSAGE =
  "Vẫn trong thời gian chờ chủ nhóm phản hồi — chưa thể tiến hành.";
export const CONSENSUS_NOT_REACHED_MESSAGE =
  "Nhóm chưa đồng thuận — không thể thực hiện.";
export const SELF_APPROVAL_FORBIDDEN_MESSAGE =
  "Người đề nghị không thể tự phê duyệt đề nghị của chính mình.";
export const LIFECYCLE_NOT_FOUND_MESSAGE = "Không tìm thấy yêu cầu.";
export const DECEASED_SUBJECT_MISMATCH_MESSAGE =
  "Phiên biểu quyết này không dành cho người này — hãy mở phiên mới cho đúng người.";
export const DECEASED_SUBJECT_IS_SELF_MESSAGE =
  "Bạn không thể tự mở hoặc áp quyết định xoá nợ cho chính suất của mình.";
export const DECEASED_SUBJECT_CANNOT_VOTE_MESSAGE =
  "Suất của người đã mất không bỏ phiếu trong phiên xoá nợ của chính suất đó.";
export const DECEASED_FORGIVE_NEEDS_NEW_SESSION_MESSAGE =
  "Phiên này đã xoá nợ của người cho nợ này rồi — khoản nợ mới cần một phiên biểu quyết mới.";
